// Payment Tracking convergence — per-loan servicing-fee (TPS platform fee)
// configuration. Effective-dated history; editable by Loans.Manage holders.
// DeductFromPayment sizes the WF1 P1 obligation; ChargeToBorrower rides on
// top of the scheduled payment (the future FCI Update-Charges seam).

import { useState } from "react";
import type { FormEvent } from "react";
import { Button, Card, StatusBadge } from "@/design-system/components/ui";
import { Input, Label } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { formatCalendarDate, isoDateInTz, useUserTimeZone } from "@/utils/datetime";
import { hasCalendarDayArrived } from "@/design-system/lib/format";
import { useAuth } from "@/hooks/useAuth";
import {
  useCreateServicingFeeConfig,
  useServicingFeeConfigs,
} from "@/hooks/portfolioAdmin/useServicing";
import { useRestampLoanTpsFee } from "@/hooks/portfolioAdmin/useTps";
import { extractErrorMessage } from "@/api/client";
import type {
  LoanServicingFeeConfigDto,
  ServicingFeeAmountType,
  ServicingFeeMode,
} from "@/api/portfolioAdmin/servicingApi";
import { fmtMoney } from "./servicingFormat";

const MODE_LABEL: Record<ServicingFeeMode, string> = {
  deduct_from_payment: "Deducted from payment",
  charge_to_borrower: "Charged to borrower",
};

function describe(config: LoanServicingFeeConfigDto): string {
  const amount =
    config.amountType === "rate_per_annum"
      ? `${(config.amount * 100).toFixed(3).replace(/\.?0+$/, "")}% p.a. on balance`
      : `${fmtMoney(config.amount)} / month`;
  return `${amount} · ${MODE_LABEL[config.mode]}`;
}

export function ServicingFeeConfigCard({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const canManage = auth.hasPermission("Loans.Manage") || auth.hasRoleCode("ADMIN");
  const configs = useServicingFeeConfigs(loanId);
  const [modalOpen, setModalOpen] = useState(false);
  const [restampOpen, setRestampOpen] = useState(false);

  // 🔴 WHICH FEE IS THIS BORROWER ACTUALLY ON — and BOTH ARMS OF THE WINDOW
  // HAD TO MOVE TOGETHER.
  //
  // `effectiveFrom`/`effectiveTo` are WALL-CALENDAR dates. This card's own
  // history list renders them with `formatCalendarDate` (UTC), so comparing
  // the same values against an INSTANT made the card contradict itself on
  // screen: from 17:00 Phoenix a config written for tomorrow was already the
  // headline answer, captioned "from <tomorrow>" three lines below it.
  //
  // WHY EACH ARM GOT THE PREDICATE IT DID. The interval is half-open —
  // `LoanServicingFeeService.GetEffectiveAsync` is `EffectiveFrom <= asOf &&
  // (EffectiveTo == null || EffectiveTo > asOf)`, and `CreateAsync` closes the
  // prior row with `prior.EffectiveTo = request.EffectiveFrom`, so the history
  // is a CONTIGUOUS chain in which one row's end IS the next row's start.
  //   - `effectiveFrom` is the INCLUSIVE lower bound: a fee effective from the
  //     9th is in force ON the 9th → `hasCalendarDayArrived(from)`.
  //   - `effectiveTo` is the EXCLUSIVE upper bound: the row runs UP TO but NOT
  //     INCLUDING that day → `!hasCalendarDayArrived(to)`, i.e. still running
  //     while its end day has not yet arrived. That is the INVERSE of the same
  //     predicate, never `isPastCalendarDay`, which would also be false ON the
  //     end day and so would leave the row current for one day too long.
  // On the shared boundary day the predecessor reads false and the successor
  // reads true, so exactly one row matches on EVERY day — no gap, no overlap.
  //
  // 🔴 FIXING ONE ARM ALONE BREAKS IT IN BOTH DIRECTIONS, which is why they
  // are one change. `effectiveFrom` alone: after 17:00 Phoenix on the day
  // before a scheduled change, the successor has not arrived (calendar rule)
  // and the predecessor's instant end has already passed (instant rule), so
  // NEITHER matches and the card falls through to the platform-default copy —
  // it tells you a borrower with an explicit fee is on $25. `effectiveTo`
  // alone: BOTH match, and which one wins is list order.
  const effective = (configs.data ?? []).find(
    (c) =>
      hasCalendarDayArrived(c.effectiveFrom) &&
      (c.effectiveTo === null || !hasCalendarDayArrived(c.effectiveTo)),
  );

  return (
    <Card data-testid="servicing-fee-card">
      <div className="flex items-start justify-between gap-space-3">
        <div>
          <h3 className="font-display font-bold text-h3 m-0 mb-space-1">
            Servicer fee
          </h3>
          <p className="text-small m-0" data-testid="servicing-fee-current">
            {effective
              ? describe(effective)
              : "$25.00 / month · Deducted from payment (platform default)"}
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-space-2">
            <Button
              variant="text"
              size="sm"
              onClick={() => setRestampOpen(true)}
              data-testid="servicing-fee-restamp"
            >
              Re-stamp from TPS schedule
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setModalOpen(true)}
              data-testid="servicing-fee-edit"
            >
              Update fee
            </Button>
          </div>
        )}
      </div>
      {(configs.data?.length ?? 0) > 0 && (
        <ul className="m-0 mt-space-3 list-none flex flex-col gap-space-1 text-caption text-gray-mid">
          {configs.data!.map((c) => (
            <li key={c.id} className="flex items-center gap-space-2">
              <span>{describe(c)}</span>
              <span>
                from {formatCalendarDate(c.effectiveFrom)}
                {c.effectiveTo ? ` to ${formatCalendarDate(c.effectiveTo)}` : ""}
              </span>
              {!c.effectiveTo && <StatusBadge tone="ok">Current</StatusBadge>}
            </li>
          ))}
        </ul>
      )}
      {modalOpen && (
        <FeeConfigModal loanId={loanId} onClose={() => setModalOpen(false)} />
      )}
      {restampOpen && (
        <RestampFeeModal loanId={loanId} onClose={() => setRestampOpen(false)} />
      )}
    </Card>
  );
}

// SP-3 — TPS schedule edits never auto-propagate to stamped loans; this is
// the explicit copy-forward. The 400s here (no real servicer / TPS has no
// monthly fee) carry plain-language messages — surface them verbatim.
function RestampFeeModal({ loanId, onClose }: { loanId: string; onClose: () => void }) {
  const restamp = useRestampLoanTpsFee(loanId);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setError(null);
    try {
      await restamp.mutateAsync();
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Re-stamp from TPS schedule"
      description="Copies the servicer's current monthly fee onto this loan as a new effective-dated config. The loan's fee history is kept."
    >
      <div className="flex flex-col gap-space-3" data-testid="restamp-fee-modal">
        {error && (
          <p
            className="text-small text-status-error m-0"
            role="alert"
            data-testid="restamp-fee-error"
          >
            {error}
          </p>
        )}
        <div className="flex justify-end gap-space-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={restamp.isPending}
            onClick={() => void confirm()}
            data-testid="restamp-fee-confirm"
          >
            {restamp.isPending ? "Re-stamping…" : "Re-stamp fee"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function FeeConfigModal({ loanId, onClose }: { loanId: string; onClose: () => void }) {
  const mutation = useCreateServicingFeeConfig(loanId);
  const tz = useUserTimeZone();
  const [mode, setMode] = useState<ServicingFeeMode>("deduct_from_payment");
  const [amountType, setAmountType] = useState<ServicingFeeAmountType>("flat");
  const [amount, setAmount] = useState("25.00");
  const [effectiveFrom, setEffectiveFrom] = useState(
    () => isoDateInTz(new Date(), tz),
  );
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError("Enter a non-negative amount.");
      return;
    }
    try {
      await mutation.mutateAsync({
        mode,
        amountType,
        amount: parsed,
        effectiveFrom: new Date(`${effectiveFrom}T00:00:00Z`).toISOString(),
        notes: notes.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "The fee config could not be saved.");
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Update servicer fee"
      description="Effective-dated — the prior configuration is closed as of the new effective date; history stays intact."
    >
      <form onSubmit={submit} className="flex flex-col gap-space-3" data-testid="fee-config-form">
        <div className="grid grid-cols-2 gap-space-3">
          <div>
            <Label htmlFor="fee-mode">Collection mode</Label>
            <select
              id="fee-mode"
              className="w-full border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure"
              value={mode}
              onChange={(e) => setMode(e.target.value as ServicingFeeMode)}
              data-testid="fee-mode"
            >
              <option value="deduct_from_payment">Deduct from payment</option>
              <option value="charge_to_borrower">Charge to borrower (on top)</option>
            </select>
          </div>
          <div>
            <Label htmlFor="fee-amount-type">Amount type</Label>
            <select
              id="fee-amount-type"
              className="w-full border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure"
              value={amountType}
              onChange={(e) => setAmountType(e.target.value as ServicingFeeAmountType)}
              data-testid="fee-amount-type"
            >
              <option value="flat">Flat $ / month</option>
              <option value="rate_per_annum">Rate (annual fraction)</option>
            </select>
          </div>
          <div>
            <Label htmlFor="fee-amount">
              {amountType === "flat" ? "Amount ($ / month)" : "Annual rate (0.005 = 0.5%)"}
            </Label>
            <Input
              id="fee-amount"
              type="number"
              step={amountType === "flat" ? "0.01" : "0.0001"}
              min="0"
              required
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="fee-amount"
            />
          </div>
          <div>
            <Label htmlFor="fee-effective">Effective from</Label>
            <Input
              id="fee-effective"
              type="date"
              required
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <Label htmlFor="fee-notes">Notes</Label>
            <Input
              id="fee-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>
        {error && (
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-space-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending} data-testid="fee-submit">
            {mutation.isPending ? "Saving…" : "Save fee config"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
